import { create } from 'zustand';
import type { ModelEdition } from '../components/common/ModelVersionControl';
import type { ImageRole } from '../types';

export type ShowcaseMode = 'flatlay_front_back' | 'three_view';
export type ShowcaseInputMode = 'single' | 'batch';
export type ShowcaseBatchInputRole = 'front' | 'back';

export type ShowcaseFiles = Record<ImageRole, string | null>;

const DEFAULT_ASPECT_BY_MODE: Record<ShowcaseMode, string> = {
  flatlay_front_back: '3:4',
  three_view: '16:9',
};

const DEFAULT_RESOLUTION_BY_MODE: Record<ShowcaseMode, string> = {
  flatlay_front_back: '1K',
  three_view: '1K',
};

export const EMPTY_SHOWCASE_FILES: ShowcaseFiles = {
  front: null,
  back: null,
  side: null,
  model: null,
  model_fullbody: null,
  top: null,
  bottom: null,
  inner: null,
  outerwear: null,
  fabric: null,
  style_reference: null,
  unassigned: null,
  result: null,
};

interface ShowcaseState {
  mode: ShowcaseMode;
  files: ShowcaseFiles;
  aspectKeyByMode: Record<ShowcaseMode, string>;
  resolutionByMode: Record<ShowcaseMode, string>;
  selectedAspectKey: string;
  selectedResolution: string;
  isPromptEditorOpen: boolean;
  customPromptText: string;
  isPromptDirty: boolean;
  customPromptSourceSelection: string | null;
  batchFiles: string[];
  batchInputRole: ShowcaseBatchInputRole;
  inputMode: ShowcaseInputMode;
  shouldGenerateBack: boolean;
  selectedModelEdition: ModelEdition;
  setMode: (mode: ShowcaseMode) => void;
  setFile: (role: ImageRole, filePath: string | null) => void;
  swapFiles: (fromRole: ImageRole, toRole: ImageRole) => void;
  resetFiles: () => void;
  setSelectedAspectKey: (key: string) => void;
  setSelectedResolution: (resolution: string) => void;
  setPromptEditorOpen: (open: boolean) => void;
  setCustomPromptText: (text: string, dirty?: boolean, sourceSelection?: string | null) => void;
  resetPromptEditor: () => void;
  setBatchFiles: (files: string[] | ((previous: string[]) => string[])) => void;
  setBatchInputRole: (role: ShowcaseBatchInputRole) => void;
  setInputMode: (mode: ShowcaseInputMode) => void;
  setShouldGenerateBack: (value: boolean) => void;
  setSelectedModelEdition: (edition: ModelEdition) => void;
  reset: () => void;
}

export const useShowcaseStore = create<ShowcaseState>((set) => ({
  mode: 'flatlay_front_back',
  files: { ...EMPTY_SHOWCASE_FILES },
  aspectKeyByMode: { ...DEFAULT_ASPECT_BY_MODE },
  resolutionByMode: { ...DEFAULT_RESOLUTION_BY_MODE },
  selectedAspectKey: DEFAULT_ASPECT_BY_MODE.flatlay_front_back,
  selectedResolution: DEFAULT_RESOLUTION_BY_MODE.flatlay_front_back,
  isPromptEditorOpen: false,
  customPromptText: '',
  isPromptDirty: false,
  customPromptSourceSelection: null,
  batchFiles: [],
  batchInputRole: 'front',
  inputMode: 'single',
  shouldGenerateBack: false,
  selectedModelEdition: 'auto',

  setMode: (mode) =>
    set((state) => ({
      mode,
      selectedAspectKey: state.aspectKeyByMode[mode] || DEFAULT_ASPECT_BY_MODE[mode],
      selectedResolution: state.resolutionByMode[mode] || DEFAULT_RESOLUTION_BY_MODE[mode],
    })),

  setFile: (role, filePath) =>
    set((state) => ({
      files: { ...state.files, [role]: filePath },
    })),

  swapFiles: (fromRole, toRole) =>
    set((state) => ({
      files: {
        ...state.files,
        [fromRole]: state.files[toRole],
        [toRole]: state.files[fromRole],
      },
    })),

  resetFiles: () => set({ files: { ...EMPTY_SHOWCASE_FILES } }),
  setSelectedAspectKey: (selectedAspectKey) =>
    set((state) => ({
      selectedAspectKey,
      aspectKeyByMode: {
        ...state.aspectKeyByMode,
        [state.mode]: selectedAspectKey,
      },
    })),
  setSelectedResolution: (selectedResolution) =>
    set((state) => ({
      selectedResolution,
      resolutionByMode: {
        ...state.resolutionByMode,
        [state.mode]: selectedResolution,
      },
    })),
  setPromptEditorOpen: (isPromptEditorOpen) => set({ isPromptEditorOpen }),
  setCustomPromptText: (customPromptText, dirty = true, customPromptSourceSelection = null) =>
    set({ customPromptText, isPromptDirty: dirty, customPromptSourceSelection }),
  resetPromptEditor: () =>
    set({ customPromptText: '', isPromptDirty: false, customPromptSourceSelection: null, isPromptEditorOpen: false }),
  setBatchFiles: (files) =>
    set((state) => ({
      batchFiles: typeof files === 'function' ? files(state.batchFiles) : files,
    })),
  setBatchInputRole: (batchInputRole) => set({ batchInputRole }),
  setInputMode: (inputMode) => set({ inputMode }),
  setShouldGenerateBack: (shouldGenerateBack) => set({ shouldGenerateBack }),
  setSelectedModelEdition: (selectedModelEdition) => set({ selectedModelEdition }),
  reset: () => set({
    mode: 'flatlay_front_back', files: { ...EMPTY_SHOWCASE_FILES },
    aspectKeyByMode: { ...DEFAULT_ASPECT_BY_MODE }, resolutionByMode: { ...DEFAULT_RESOLUTION_BY_MODE },
    selectedAspectKey: DEFAULT_ASPECT_BY_MODE.flatlay_front_back,
    selectedResolution: DEFAULT_RESOLUTION_BY_MODE.flatlay_front_back,
    isPromptEditorOpen: false, customPromptText: '', isPromptDirty: false, customPromptSourceSelection: null, batchFiles: [],
    batchInputRole: 'front', inputMode: 'single', shouldGenerateBack: false, selectedModelEdition: 'auto',
  }),
}));
